import { httpsCallable } from 'firebase/functions'
import { functions } from './firebase'
import type {
  GenerationBundle,
  MapSceneSpecV1,
  OperationReceipt,
  SimulationSummary,
  InteractionPlanV3,
  SupportedLocale,
  ContentType,
  ExperimentSnapshot,
} from '../types/simulation'
import type { MembershipProfile } from './membership'
import type {
  BillingOffer,
  BillingPriceSource,
} from './billingCatalog'
import { logOperationReceipt } from './aiCostLogger'

interface CallableReceipt extends Omit<OperationReceipt, 'createdAt'> {
  createdAt?: unknown
}

interface AiResponse<T> {
  result: T
  receipt: CallableReceipt
  idempotentReplay?: boolean
}

export interface GenerationResult {
  operationId: string | null
  simulationId: string
  slug?: string
  bundle: GenerationBundle
  imageAssets: Array<Record<string, unknown>>
  creator: {
    id: string
    name: string
    handle: string
    avatar: string
    username?: string
  }
  version?: number
  parentId?: string | null
  rootId?: string
  sourceVersion?: number | null
  sourceTitle?: string | null
  viewCount?: number
}

export interface InputHistoryItem {
  simulationId: string
  prompt: string
  title: string
  category: string
  renderer: string
  contentType?: ContentType
  contentCategory?: string
  createdAtMs: number
  updatedAtMs: number
}

export interface ManagedCollection {
  id: string
  slug: string
  title: string
  description: string
  shelfId: string | null
  duration: string | null
  accent: string
  profile: {
    topics: string[]
    disciplines: string[]
    methods: string[]
    learningGoals: string[]
    exclusions: string[]
  }
  autoManaged: boolean
  assignedCount: number
  publicItemCount: number
  createdBy: string
  updatedAt?: unknown
}

export interface CommunityProfile {
  username: string | null
  handle: string | null
  usernameSet: boolean
  usernameSetAtMs: number
}

export interface PublicCreatorProfile {
  id: string
  username: string
  handle: string
  name: string
  avatar: string
  productionCount: number
  totalViews: number
}

export interface FollowedCreatorGroup {
  creator: PublicCreatorProfile
  items: SimulationSummary[]
}

const callPlan = httpsCallable<
  {
    prompt: string
    locale: SupportedLocale
    idempotencyKey: string
  },
  AiResponse<InteractionPlanV3>
>(functions, 'startSimulationPlanning', { limitedUseAppCheckTokens: true })

const callGenerate = httpsCallable<
  {
    prompt: string
    locale: SupportedLocale
    answers: Record<string, string>
    paletteId: string
    contentType: ContentType
    idempotencyKey: string
  },
  AiResponse<GenerationResult>
>(functions, 'generateSimulation', { timeout: 930_000, limitedUseAppCheckTokens: true })

export const liveAiEnabled =
  import.meta.env.VITE_ENABLE_LIVE_AI === 'true'

export async function planSimulation(
  prompt: string,
  locale: SupportedLocale,
) {
  const response = await callPlan({
    prompt,
    locale,
    idempotencyKey: crypto.randomUUID(),
  })
  return {
    ...response.data,
    receipt: normalizeReceipt(response.data.receipt),
  }
}

export async function generateSimulation(
  prompt: string,
  locale: SupportedLocale,
  answers: Record<string, string>,
  paletteId: string,
  contentType: ContentType = 'simulation',
) {
  const response = await callGenerate({
    prompt,
    locale,
    answers,
    paletteId,
    contentType,
    idempotencyKey: crypto.randomUUID(),
  })
  // A completed production is published by the backend immediately. Do not
  // keep serving the pre-production community snapshot for another 30s.
  invalidateCommunityFeedCache()
  return {
    ...response.data,
    receipt: normalizeReceipt(response.data.receipt),
  }
}

const callGetSimulation = httpsCallable<
  { simulationId: string },
  GenerationResult
>(functions, 'getSimulation', { limitedUseAppCheckTokens: true })

export async function getGeneratedSimulation(simulationId: string) {
  const response = await callGetSimulation({ simulationId })
  return response.data
}

const callTranslateSimulation = httpsCallable<
  {
    simulationId: string
    locale: SupportedLocale
    idempotencyKey: string
  },
  {
    result: GenerationResult
    receipt?: CallableReceipt
    cacheHit: boolean
    idempotentReplay?: boolean
  }
>(functions, 'translateSimulation', {
  timeout: 420_000,
  limitedUseAppCheckTokens: true,
})

export async function translateGeneratedSimulation(
  simulationId: string,
  locale: SupportedLocale,
) {
  const response = await callTranslateSimulation({
    simulationId,
    locale,
    idempotencyKey: crypto.randomUUID(),
  })
  return {
    ...response.data,
    ...(response.data.receipt
      ? { receipt: normalizeReceipt(response.data.receipt) }
      : {}),
  }
}

const callReportArtifactRuntimeFault = httpsCallable<
  {
    simulationId: string
    operationId: string
    contentHash: string
    kind: 'runtime-error'
    errors: string[]
  },
  {
    accepted: boolean
    refunded: boolean
    destroyed: boolean
    idempotentReplay: boolean
  }
>(functions, 'reportArtifactRuntimeFault', { limitedUseAppCheckTokens: true })

export async function reportArtifactRuntimeFault(input: {
  simulationId: string
  operationId: string
  contentHash: string
  kind: 'runtime-error'
  errors: string[]
}) {
  return (await callReportArtifactRuntimeFault(input)).data
}

const callRecordSimulationView = httpsCallable<
  { simulationId: string },
  { simulationId: string; viewCount: number }
>(functions, 'recordSimulationView', { limitedUseAppCheckTokens: true })

export async function recordSimulationView(simulationId: string) {
  return (await callRecordSimulationView({ simulationId })).data
}

const callGetMapScene = httpsCallable<
  { simulationId: string },
  {
    mapScene: MapSceneSpecV1 | null
    session: { runtimeUrl: string; token: string; expiresAt: unknown }
  }
>(functions, 'getMapScene', { limitedUseAppCheckTokens: true })

export async function getMapScene(simulationId: string) {
  return (await callGetMapScene({ simulationId })).data
}

const callMapUsage = httpsCallable<
  {
    operationId: string
    simulationId: string
    event: 'mapRuntimeStarted' | 'dynamicMapLoaded' | 'mapLayerLoaded' | 'mapInteraction' | 'mapProviderError' | 'mapRuntimeClosed'
    layers: string[]
    providerStatus?: string
  },
  { ok: boolean }
>(functions, 'logMapUsage', { limitedUseAppCheckTokens: true })

export async function logMapUsage(input: {
  operationId: string
  simulationId: string
  event: 'mapRuntimeStarted' | 'dynamicMapLoaded' | 'mapLayerLoaded' | 'mapInteraction' | 'mapProviderError' | 'mapRuntimeClosed'
  layers: string[]
  providerStatus?: string
}) {
  return (await callMapUsage(input)).data
}

const callWorkspace = httpsCallable<
  { locale: SupportedLocale; nativeStore?: 'ios' | 'android' },
  {
    credits: number
    membership: MembershipProfile
    profile: CommunityProfile
    created?: SimulationSummary[]
    saved?: SimulationSummary[]
    savedIds?: string[]
    likedIds?: string[]
    followingCreatorIds?: string[]
    collections?: Array<Record<string, unknown>>
    snapshots?: ExperimentSnapshot[]
  }
>(functions, 'getUserWorkspace', { timeout: 12_000, limitedUseAppCheckTokens: true })

const callMembershipState = httpsCallable<
  { nativeStore?: 'ios' | 'android'; forceStoreVerification?: boolean },
  { membership: MembershipProfile; credits: number }
>(functions, 'getMembershipState', { timeout: 12_000, limitedUseAppCheckTokens: true })

export async function getMembershipState(nativeStore?: 'ios' | 'android', forceStoreVerification = false) {
  return (await callMembershipState({ ...(nativeStore ? { nativeStore } : {}), forceStoreVerification })).data
}

export async function getUserWorkspace(locale: SupportedLocale = 'tr', nativeStore?: 'ios' | 'android') {
  const workspace = (await callWorkspace({ locale, ...(nativeStore ? { nativeStore } : {}) })).data
  return {
    ...workspace,
    created: arrayOrEmpty(workspace.created),
    saved: arrayOrEmpty(workspace.saved),
    savedIds: arrayOrEmpty(workspace.savedIds),
    likedIds: arrayOrEmpty(workspace.likedIds),
    followingCreatorIds: arrayOrEmpty(workspace.followingCreatorIds),
    collections: arrayOrEmpty(workspace.collections),
    snapshots: arrayOrEmpty(workspace.snapshots),
  }
}

function arrayOrEmpty<T>(value: T[] | undefined): T[] {
  return Array.isArray(value) ? value : []
}

const callBillingCatalog = httpsCallable<
  Record<string, never>,
  {
    version?: string
    source: BillingPriceSource
    updatedAtMs: number
    offers: BillingOffer[]
  }
>(functions, 'getBillingCatalog')

export async function getBillingCatalog() {
  return (await callBillingCatalog({})).data
}

const callCreateStripeCheckoutSession = httpsCallable<
  { productId: string },
  { url: string; mode: 'checkout' | 'portal' }
>(functions, 'createStripeCheckoutSession', {
  limitedUseAppCheckTokens: true,
})

export async function createStripeCheckoutSession(productId: string) {
  return (await callCreateStripeCheckoutSession({ productId })).data
}

const callCreateStripePortalSession = httpsCallable<
  Record<string, never>,
  { url: string }
>(functions, 'createStripePortalSession', {
  limitedUseAppCheckTokens: true,
})

export async function createStripePortalSession() {
  return (await callCreateStripePortalSession({})).data
}

const callSetCommunityUsername = httpsCallable<
  { username: string },
  {
    username: string
    handle: string
    usernameSet: boolean
  }
>(functions, 'setCommunityUsername', { limitedUseAppCheckTokens: true })

export async function setCommunityUsername(username: string) {
  return (await callSetCommunityUsername({ username })).data
}

const callInputHistory = httpsCallable<
  Record<string, never>,
  { items: InputHistoryItem[] }
>(functions, 'getInputHistory', { limitedUseAppCheckTokens: true })

export async function getInputHistory() {
  return (await callInputHistory({})).data.items
}

const callCategoryFeed = httpsCallable<
  {
    categoryId: string
    contentType: ContentType
    locale: SupportedLocale
    sort: 'newest' | 'popular'
  },
  {
    categoryId: string
    items: SimulationSummary[]
  }
>(functions, 'getCategoryFeed', { limitedUseAppCheckTokens: true })

export async function getCategoryFeed(
  categoryId: string,
  contentType: ContentType = 'simulation',
  locale: SupportedLocale = 'tr',
  sort: 'newest' | 'popular' = 'newest',
) {
  return (
    await callCategoryFeed({ categoryId, contentType, locale, sort })
  ).data.items
}

const callCommunityFeed = httpsCallable<
  { contentType: ContentType; locale: SupportedLocale },
  { items: SimulationSummary[] }
>(functions, 'getCommunityFeed', { limitedUseAppCheckTokens: true })

const communityFeedCache = new Map<
  string,
  { expiresAt: number; items: SimulationSummary[] }
>()
const communityFeedInflight = new Map<
  string,
  Promise<SimulationSummary[]>
>()

export function invalidateCommunityFeedCache() {
  communityFeedCache.clear()
  communityFeedInflight.clear()
}

export async function getCommunityFeed(
  contentType: ContentType = 'simulation',
  locale: SupportedLocale = 'tr',
) {
  const key = `${contentType}:${locale}`
  const cached = communityFeedCache.get(key)
  if (cached && cached.expiresAt > Date.now()) return cached.items
  const pending = communityFeedInflight.get(key)
  if (pending) return pending
  const request = callCommunityFeed({ contentType, locale })
    .then((response) => {
      communityFeedCache.set(key, {
        expiresAt: Date.now() + 30_000,
        items: response.data.items,
      })
      return response.data.items
    })
    .finally(() => communityFeedInflight.delete(key))
  communityFeedInflight.set(key, request)
  return request
}

const callCreatorProfile = httpsCallable<
  { username: string; locale: SupportedLocale },
  {
    creator: PublicCreatorProfile
    items: SimulationSummary[]
  }
>(functions, 'getCreatorProfile', { limitedUseAppCheckTokens: true })

export async function getCreatorProfile(
  username: string,
  locale: SupportedLocale = 'tr',
) {
  return (await callCreatorProfile({ username, locale })).data
}

const callFollowingFeed = httpsCallable<
  { locale: SupportedLocale },
  { creators: FollowedCreatorGroup[] }
>(functions, 'getFollowingFeed', { limitedUseAppCheckTokens: true })

export async function getFollowingFeed(locale: SupportedLocale = 'tr') {
  return (await callFollowingFeed({ locale })).data.creators
}

const callSearchCatalog = httpsCallable<
  { query: string; limit: number; locale: SupportedLocale },
  {
    simulations: SimulationSummary[]
    collections: Array<{
      id: string
      title: string
      description: string
      slug: string
    }>
  }
>(functions, 'searchCatalog', { limitedUseAppCheckTokens: true })

export async function searchCatalog(
  query: string,
  locale: SupportedLocale = 'tr',
) {
  return (await callSearchCatalog({ query, limit: 20, locale })).data
}

const callCollectionsCatalog = httpsCallable<
  { contentType: ContentType },
  { items: ManagedCollection[] }
>(functions, 'getCollectionsCatalog', { limitedUseAppCheckTokens: true })

export async function getCollectionsCatalog(contentType: ContentType = 'simulation') {
  return (await callCollectionsCatalog({ contentType })).data.items
}

const callCollectionDetail = httpsCallable<
  {
    idOrSlug: string
    contentType: ContentType
    locale: SupportedLocale
  },
  { collection: ManagedCollection; simulations: SimulationSummary[] }
>(functions, 'getCollectionDetail', { limitedUseAppCheckTokens: true })

export async function getCollectionDetail(
  idOrSlug: string,
  contentType: ContentType = 'simulation',
  locale: SupportedLocale = 'tr',
) {
  return (
    await callCollectionDetail({ idOrSlug, contentType, locale })
  ).data
}

const callSubmitGameScore = httpsCallable<
  {
    simulationId: string
    score: number
    correct?: number
    wrong?: number
    remainingSeconds?: number
  },
  {
    ok: boolean
    verified: false
    bestScore: number
    improved: boolean
    direction: 'higher' | 'lower'
  }
>(functions, 'submitGameScore', { limitedUseAppCheckTokens: true })

export async function submitGameScore(
  simulationId: string,
  score: number,
  result?: {
    correct?: number
    wrong?: number
    remainingSeconds?: number
  },
) {
  return (await callSubmitGameScore({ simulationId, score, ...result })).data
}

export interface GameLeaderboard {
  verified: false
  scoreMetric: string
  scoreDirection: 'higher' | 'lower'
  contentType: ContentType
  items: Array<{
    rank: number
    userId: string
    score: number
    player: { name?: string; handle?: string; avatar?: string }
    result?: {
      correct?: number | null
      wrong?: number | null
      remainingSeconds?: number | null
    } | null
  }>
  personalBest: number | null
  personalRank: number | null
  personalResult?: {
    correct?: number | null
    wrong?: number | null
    remainingSeconds?: number | null
  } | null
}

const callGetGameLeaderboard = httpsCallable<
  { simulationId: string },
  GameLeaderboard
>(functions, 'getGameLeaderboard', { limitedUseAppCheckTokens: true })

export async function getGameLeaderboard(simulationId: string) {
  return (await callGetGameLeaderboard({ simulationId })).data
}

const callSetInteraction = httpsCallable<
  { type: 'save' | 'like' | 'follow'; targetId: string; enabled: boolean },
  { ok: boolean; enabled: boolean }
>(functions, 'setInteraction', { limitedUseAppCheckTokens: true })

export async function setRemoteInteraction(
  type: 'save' | 'like' | 'follow',
  targetId: string,
  enabled: boolean,
) {
  return (await callSetInteraction({ type, targetId, enabled })).data
}

const callSaveSnapshot = httpsCallable<
  {
    simulationId: string
    values: Record<string, number>
    artifactState?: Record<string, unknown>
    progress: number
  },
  { ok: boolean }
>(functions, 'saveExperimentSnapshot', { limitedUseAppCheckTokens: true })

export async function saveRemoteSnapshot(snapshot: {
  simulationId: string
  values: Record<string, number>
  artifactState?: Record<string, unknown>
  progress: number
}) {
  return (await callSaveSnapshot(snapshot)).data
}

const callPublishSimulation = httpsCallable<
  { simulationId: string; idempotencyKey: string },
  { ok: boolean; publicLaunch: boolean; username: string }
>(functions, 'publishSimulation', { limitedUseAppCheckTokens: true })

export async function publishRemoteSimulation(simulationId: string) {
  return (await callPublishSimulation({
    simulationId,
    idempotencyKey: crypto.randomUUID(),
  })).data
}

function normalizeReceipt(receipt: CallableReceipt): OperationReceipt {
  const source = receipt.createdAt as
    | { toDate?: () => Date; seconds?: number; _seconds?: number }
    | string
    | undefined
  let createdAt = new Date().toISOString()
  if (typeof source === 'string') createdAt = source
  else if (source && typeof source.toDate === 'function') {
    createdAt = source.toDate().toISOString()
  } else if (source && typeof (source.seconds ?? source._seconds) === 'number') {
    createdAt = new Date(
      Number(source.seconds ?? source._seconds) * 1000,
    ).toISOString()
  }
  const normalized = { ...receipt, createdAt } as OperationReceipt
  logOperationReceipt(normalized)
  return normalized
}
